"use client";

import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useShallow } from "zustand/react/shallow";
import { useCreatePageStore } from "@/stores/createPageStore";
import { diffDesignSpecFields } from "@/lib/agent/designSpecSnapshot";
import {
  isDesignWorkspaceBusy,
  planDesignChanges,
  planDesignChangesUndo,
  type DesignRenderSide,
} from "@/lib/designer/applyDesignChanges";
import {
  buildDesignPreviewUpdatePrompt,
  getSideRelevantDesignChanges,
} from "@/lib/designer/previewFreshness";
import { restoreConceptDecisions } from "@/lib/designer/restoreDesignDraft";
import { calculateHasPrompt } from "@/stores/utils";
import { useChatActions } from "./ChatActionsProvider";

type ApplyOptions = {
  /** A chained action owns the next step instead of the automatic chat review. */
  suppressAgentReview?: boolean;
};

type ExecutionState = {
  projectEpoch: number;
  busyStep: DesignRenderSide | null;
  failedSide: DesignRenderSide | null;
  pendingSides?: DesignRenderSide[];
};
const EMPTY_EXECUTION: ExecutionState = {
  projectEpoch: -1,
  busyStep: null,
  failedSide: null,
};
let execution = EMPTY_EXECUTION;
let runNumber = 0;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
function publish(next: ExecutionState) {
  execution = next;
  listeners.forEach(listener => listener());
}
const imageKey = (image: { url: string | null; path: string | null }) =>
  JSON.stringify([image.url, image.path]);

/** A single committed draft, one paid side at a time, shared across view mounts. */
export function useApplyDesignChanges() {
  const { modifyImage, generateImages } = useChatActions();
  const {
    formData,
    designBrief,
    phase,
    images,
    projectEpoch,
    workspaceBusy,
    rendering,
    renderPhase,
  } = useCreatePageStore(
    useShallow(state => ({
      formData: state.form.data,
      designBrief: state.agent.designBrief,
      phase: state.agent.phase,
      images: state.generation.images,
      projectEpoch: state.projectActionEpoch,
      workspaceBusy: isDesignWorkspaceBusy(state),
      rendering:
        state.generation.progress.loading || state.ui.modification.isModifying,
      renderPhase: state.generation.progress.phase,
    }))
  );
  const active = useSyncExternalStore(
    subscribe,
    () => execution,
    () => EMPTY_EXECUTION
  );
  const input = useMemo(
    () => ({
      form: { data: formData },
      agent: { designBrief, phase },
      generation: { images },
    }),
    [formData, designBrief, phase, images]
  );
  const plan = useMemo(() => planDesignChanges(input), [input]);
  const undoPlan = useMemo(
    () => planDesignChangesUndo(input, plan),
    [input, plan]
  );
  const owned = active.projectEpoch === projectEpoch;
  const busyStep = owned ? active.busyStep : null;
  const busy = Boolean(busyStep) || workspaceBusy;

  const apply = useCallback(
    async (applyOptions?: ApplyOptions) => {
      const current = useCreatePageStore.getState();
      if (
        isDesignWorkspaceBusy(current) ||
        (execution.projectEpoch === current.projectActionEpoch &&
          execution.busyStep)
      )
        return false;
      const captured = planDesignChanges(current);
      if (
        !captured.draft ||
        !captured.steps.length ||
        captured.unavailableReason
      )
        return false;
      const draft = captured.draft;
      const epoch = current.projectActionEpoch;
      const run = ++runNumber;
      const expected = {
        front: imageKey(current.generation.images.front),
        back: imageKey(current.generation.images.back),
      };
      let failure: DesignRenderSide | null = null;
      let completedSteps = 0;
      const isCurrent = () =>
        runNumber === run &&
        useCreatePageStore.getState().projectActionEpoch === epoch;
      const unsubscribe = useCreatePageStore.subscribe(next => {
        if (next.projectActionEpoch !== epoch && runNumber === run) {
          runNumber += 1;
          publish(EMPTY_EXECUTION);
        }
      });

      try {
        // The customer has now committed the priced batch. Retain its new-back
        // request if the front saves this draft before the back fails or retries.
        if (
          !current.generation.images.back.url &&
          captured.steps.some(
            step => step.side === "back" && step.mode === "generate"
          )
        )
          current.updateFormData({ generateBack: true });
        for (const [index, step] of captured.steps.entries()) {
          if (!isCurrent()) break;
          const before = useCreatePageStore.getState();
          // A variation switch changes the source the customer is looking at.
          // Never chain a second charge against a different selected dress.
          if (
            (["front", "back"] as const).some(
              side =>
                imageKey(before.generation.images[side]) !== expected[side]
            )
          )
            break;
          publish({
            projectEpoch: epoch,
            busyStep: step.side,
            failedSide: null,
            pendingSides: captured.pendingSides,
          });
          const options = {
            side: step.side,
            suppressAgentReview:
              applyOptions?.suppressAgentReview === true ||
              index < captured.steps.length - 1,
            ...(step.side === "back" &&
            step.mode === "generate" &&
            Boolean(before.generation.images.back.url)
              ? { regenerateBackFromFront: true }
              : {}),
          };
          let stopObserving: () => void = () => {};
          const stopped = new Promise<void>(resolve => {
            let started = false;
            stopObserving = useCreatePageStore.subscribe(next => {
              if (!isCurrent()) {
                resolve();
                return;
              }
              const rendering =
                next.generation.progress.loading ||
                next.ui.modification.isModifying;
              if (rendering) started = true;
              // The existing watchdog can finish the UI even when its transport
              // never settles. Release this batch at that same failure boundary.
              if (started && !rendering && next.generation.errors.message)
                resolve();
            });
          });
          try {
            const dispatched =
              step.mode === "modify"
                ? modifyImage(
                    buildDesignPreviewUpdatePrompt(step.side, step.changes),
                    draft,
                    options
                  )
                : generateImages(
                    step.side === "front" ? 2 : 4,
                    true,
                    draft,
                    options
                  );
            await Promise.race([dispatched, stopped]);
          } catch {
            // Action hooks normally render their own errors. Preserve that state
            // and retain the pending side when an adapter rejects unexpectedly.
            if (isCurrent())
              useCreatePageStore
                .getState()
                .setError(
                  `The ${step.side} preview could not be updated. Try again.`
                );
          } finally {
            stopObserving();
          }
          if (!isCurrent()) break;
          const after = useCreatePageStore.getState();
          const image = after.generation.images[step.side];
          const succeeded = Boolean(
            image.url &&
            image.path &&
            image.designSpec &&
            imageKey(image) !== expected[step.side] &&
            !getSideRelevantDesignChanges(
              diffDesignSpecFields(image.designSpec, draft),
              step.side
            ).length
          );
          if (!succeeded) {
            failure = step.side;
            break;
          }
          expected[step.side] = imageKey(image);
          completedSteps += 1;
        }
      } finally {
        unsubscribe();
        if (isCurrent())
          publish({ projectEpoch: epoch, busyStep: null, failedSide: failure });
      }
      const latest = useCreatePageStore.getState();
      // A Front update may deliberately leave the saved Back preview behind for
      // a later, separately priced update. Follow-up actions may use the
      // completed step as long as the committed draft and image selection did
      // not change while it ran.
      return Boolean(
        isCurrent() &&
        completedSteps === captured.steps.length &&
        !latest.generation.errors.message &&
        JSON.stringify(planDesignChanges(latest).draft) ===
          JSON.stringify(draft) &&
        (["front", "back"] as const).every(
          side => imageKey(latest.generation.images[side]) === expected[side]
        )
      );
    },
    [generateImages, modifyImage]
  );

  const undo = useCallback(() => {
    const current = useCreatePageStore.getState();
    if (
      isDesignWorkspaceBusy(current) ||
      (execution.projectEpoch === current.projectActionEpoch &&
        execution.busyStep)
    )
      return;
    const restored = planDesignChangesUndo(current);
    if (!restored) return;
    restored.designBrief.activeConcept =
      restoreConceptDecisions(
        current.agent.designBrief?.activeConcept,
        restored.saved
      ) ?? undefined;
    const timestamp = new Date();
    const messageId = `revert-${timestamp.getTime()}-${Math.random().toString(36).slice(2)}`;
    // Restore form and conversational memory together so no subscriber sees
    // an intermediate draft that still requests generation or contradicts it.
    useCreatePageStore.setState({
      form: {
        ...current.form,
        data: restored.formData,
        hasPrompt: calculateHasPrompt(restored.formData),
        showValidationError: false,
      },
      agent: {
        ...current.agent,
        designBrief: restored.designBrief,
        actionPlan: null,
        presets: [],
        conversation: {
          ...current.agent.conversation,
          error: null,
          messages: [
            ...current.agent.conversation.messages.map(message => ({
              ...message,
              widgets: message.widgets?.map(widget =>
                widget.active ? { ...widget, active: false } : widget
              ),
            })),
            {
              id: `${messageId}-request`,
              role: "user",
              content: "Revert pending changes to the generated design.",
              hidden: true,
              timestamp,
            },
            {
              id: `${messageId}-result`,
              role: "assistant",
              content:
                "Reverted pending changes to the generated design. Would you like to try it on or get a quote?",
              timestamp,
            },
          ],
        },
        activeConcept:
          restoreConceptDecisions(
            current.agent.activeConcept,
            restored.saved
          ) ?? null,
        phase: restored.phase,
      },
      generation: {
        ...current.generation,
        errors: { ...current.generation.errors, message: null },
        progress: {
          ...current.generation.progress,
          hasFailedGeneration: false,
        },
      },
    });
    publish(EMPTY_EXECUTION);
  }, []);

  return {
    ...plan,
    pendingSides:
      busyStep && active.pendingSides ? active.pendingSides : plan.pendingSides,
    creditCost:
      busyStep && active.pendingSides
        ? active.pendingSides.length
        : plan.creditCost,
    busy,
    busyStep:
      busyStep ??
      (rendering && (renderPhase === "front" || renderPhase === "back")
        ? renderPhase
        : null),
    failedSide:
      owned &&
      active.failedSide &&
      plan.pendingSides.includes(active.failedSide)
        ? active.failedSide
        : null,
    canApply: !busy && plan.steps.length > 0 && !plan.unavailableReason,
    canUndo: !busy && Boolean(undoPlan),
    apply,
    undo,
  };
}
